// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useMemo } from 'react';

import type { EvolveApiRoute, EvolveApiResponse, WrappedPaginatedGet } from '@evolvemep/api-ts';
import { Flex } from '@mantine/core';

import { KanbanLane } from 'components/Mantine/Kanban/KanbanLane';
import type { CardConfigMapper } from 'components/Mantine/Kanban/types';
import type { KeysOfType, WithOptional } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';

type Props<
  LaneRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  Lane extends ([LaneRoute] extends [never] ? unknown : EvolveApiResponse<'GET_PAGINATED', LaneRoute>['data'][number]),
  LaneKey extends KeysOfType<Lane, string>,
  CardRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  Card extends EvolveApiResponse<'GET_PAGINATED', CardRoute>['data'][number],
  CardKey extends KeysOfType<Card, string>,
> = {
  /** Callback to render the kanban cards within their respective lane. */
  children: (card: Card, index: number, lane: Lane) => React.ReactNode;
  /** The key of the ID property of the kanban lane. The value of the property must be a string. */
  laneIdKey: LaneKey;
  /** Callback to get the label for a given lane. */
  getLaneLabel: (lane: Lane) => string;
  /** Optional callback to get the color for a given lane. If provided, a border with this color will be rendered at the top of the lane. */
  getLaneColor?: (lane: Lane) => string;
  /** The lanes to render. Use this to control the state of the lanes. */
  lanes?: Lane[];
  /** Response from useEvolvePaginatedGet to fetch the kanban lanes. Will be ignored if `lanes` is set. */
  paginatedGetLanes?: WithOptional<WrappedPaginatedGet<LaneRoute>, 'setDefaultOpts'>;
  /** Optional callback invoked when a card is dragged and dropped to a new position. */
  onDragEnd?: (cardId: Card[CardKey], precedingCardId?: Card[CardKey]) => Promise<void>;
  /** Optional tooltip text to display on the lane when dragging a card. */
  draggingTooltip?: string;
  /** The key of the ID property of the kanban card. The value of the property must be a string. */
  cardIdKey: CardKey;
  /** Callback used to get config for fetching new cards. */
  cardConfigMapper: CardConfigMapper<Lane, CardRoute>;
};

export const KanbanBoard = <
  LaneRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  Lane extends ([LaneRoute] extends [never] ? unknown : EvolveApiResponse<'GET_PAGINATED', LaneRoute>['data'][number]),
  LaneKey extends KeysOfType<Lane, string>,
  CardRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  Card extends EvolveApiResponse<'GET_PAGINATED', CardRoute>['data'][number],
  CardKey extends KeysOfType<Card, string>,
>({
  children,
  laneIdKey,
  getLaneLabel,
  getLaneColor,
  lanes: providedLanes,
  paginatedGetLanes,
  onDragEnd,
  draggingTooltip,
  cardIdKey,
  cardConfigMapper,
}: Props<LaneRoute, Lane, LaneKey, CardRoute, Card, CardKey>) => {
  const { data: laneData, fetchNextPage, loading } = paginatedGetLanes ?? {};
  const lanes = useMemo(() => (providedLanes ?? laneData ?? []) as Lane[], [providedLanes, laneData]);

  return (
    <Flex gap="xl" pb={4} style={{ height: '100%', overflowX: 'auto' }}>
      {lanes.map((lane) => {
        const label = getLaneLabel(lane);
        const color = getLaneColor?.(lane);
        return (
          <KanbanLane<LaneRoute, Lane, CardRoute, Card, CardKey>
            key={String(lane[laneIdKey])}
            id={String(lane[laneIdKey])}
            lane={lane}
            label={label}
            color={color}
            onDragEnd={onDragEnd}
            draggingTooltip={draggingTooltip}
            cardIdKey={cardIdKey}
            cardConfigMapper={cardConfigMapper}
          >
            {(cards) => cards.map((card, cardIndex) => children(card, cardIndex, lane))}
          </KanbanLane>
        );
      })}
      {isNotNil(fetchNextPage) && (
        <Flex flex="1 0 0" align="center" justify="center">
          <TriggerOnVisible loaderProps={{ type: 'bars' }} onVisible={fetchNextPage} loading={loading} />
        </Flex>
      )}
    </Flex>
  );
};
